import { countries } from "country-data-list";
import type { CountryCode } from "libphonenumber-js";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { InputMask } from "@/components/fields/mask-input";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PhoneHelpers } from "@/lib/phone";
import { cn } from "@/lib/utils";

export type Country = {
  alpha2: string;
  alpha3: string;
  countryCallingCodes: Array<string>;
  currencies: Array<string>;
  emoji?: string;
  ioc: string;
  languages: Array<string>;
  name: string;
  status: string;
};

class CountryNameLocalizer {
  private displayNames: Intl.DisplayNames | null = null;
  private locale: string = "es-AR";

  constructor(locale: string = "es-AR") {
    this.locale = locale;
    this.initializeDisplayNames();
  }

  private initializeDisplayNames() {
    try {
      this.displayNames = new Intl.DisplayNames([this.locale], {
        type: "region",
        fallback: "code",
      });
    } catch {
      this.displayNames = null;
    }
  }

  getLocalizedCountryName(countryCode: string, fallbackName: string): string {
    if (!this.displayNames) {
      return fallbackName;
    }

    try {
      const localizedName = this.displayNames.of(countryCode.toUpperCase());
      return localizedName || fallbackName;
    } catch {
      return fallbackName;
    }
  }

  setLocale(newLocale: string) {
    if (newLocale !== this.locale) {
      this.locale = newLocale;
      this.initializeDisplayNames();
    }
  }
}

function getPhoneMaskPattern(countryCode: string): string {
  switch (countryCode) {
    case "BR":
      return "(99) 99999-9999";
    case "US":
    case "CA":
      return "(999) 999-9999";
    case "GB":
      return "9999 999 9999";
    case "DE":
      return "999 999 99999";
    case "FR":
      return "99 99 99 99 99";
    case "IT":
      return "999 999 9999";
    case "ES":
      return "999 99 99 99";
    case "AU":
      return "9999 999 999";
    case "JP":
      return "999-9999-9999";
    case "KR":
      return "999-9999-9999";
    case "CN":
      return "999 9999 9999";
    case "IN":
      return "99999 99999";
    case "MX":
      return "999 999 9999";
    case "AR":
      return "999 999-9999";
    case "PT":
      return "999 999 999";
    case "UY":
      return "9999 9999";
    case "PY":
      return "999 999 999";
    case "CL":
      return "9 9999 9999";
    case "CO":
      return "999 999 9999";
    case "PE":
      return "999 999 999";
    case "EC":
      return "99 999 9999";
    case "VE":
      return "999 999 9999";
    case "BO":
      return "9 999 9999";
    default:
      return "999 999 9999";
  }
}

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  defaultCountry?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  locale?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
}

const PhoneInputComponent = forwardRef<HTMLDivElement, PhoneInputProps>(function PhoneInputComponent(
  {
    value = "",
    onChange,
    defaultCountry = "ARG",
    className,
    disabled = false,
    error = false,
    locale = "es-AR",
    searchPlaceholder,
    noResultsText,
  },
  ref,
) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [displayNumber, setDisplayNumber] = useState("");
  const debounceTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isInternalChangeRef = useRef(false);

  const resolvedSearchPlaceholder = searchPlaceholder ?? t("phone_input.search_placeholder");
  const resolvedNoResultsText = noResultsText ?? t("phone_input.no_results");

  const localizer = useMemo(() => new CountryNameLocalizer(locale), [locale]);

  const countryOptions = useMemo(() => {
    const validCountries = countries.all.filter(
      (country: Country) => country.countryCallingCodes.length > 0 && country.status !== "deleted" && country.alpha2,
    );

    return validCountries.sort((a, b) => {
      const nameA = localizer.getLocalizedCountryName(a.alpha2, a.name);
      const nameB = localizer.getLocalizedCountryName(b.alpha2, b.name);
      return nameA.localeCompare(nameB, locale);
    });
  }, [localizer, locale]);

  const initialCountry = useMemo(
    () =>
      countryOptions.find((country) => country.alpha3 === defaultCountry) ||
      countryOptions.find((country) => country.alpha3 === "ARG") ||
      countryOptions[0],
    [countryOptions, defaultCountry],
  );

  const [selectedCountry, setSelectedCountry] = useState<Country>(initialCountry);

  const maskPattern = useMemo(() => getPhoneMaskPattern(selectedCountry.alpha2), [selectedCountry.alpha2]);
  const maskPlaceholder = useMemo(() => maskPattern.replace(/9/g, "0"), [maskPattern]);

  const getLocalizedCountryName = useCallback(
    (country: Country): string => {
      return localizer.getLocalizedCountryName(country.alpha2, country.name);
    },
    [localizer],
  );

  // Parse E.164 value from outside (e.g. form default, server data)
  // Skip when the change originated from user typing inside this component
  useEffect(() => {
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }

    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed?.country) {
        const matchedCountry = countryOptions.find((c) => c.alpha2 === parsed.country);
        if (matchedCountry) {
          setSelectedCountry(matchedCountry);
        }
        const nationalNumber = parsed.nationalNumber;
        setPhoneNumber(nationalNumber);
        setDisplayNumber(nationalNumber);
      } else {
        const cleanNumber = value.replace(/\D/g, "");
        setPhoneNumber(cleanNumber);
        setDisplayNumber(cleanNumber);
      }
    } else {
      setPhoneNumber("");
      setDisplayNumber("");
    }
  }, [value, countryOptions]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const emitValue = useCallback(
    (cleanNumber: string, country: Country) => {
      isInternalChangeRef.current = true;

      if (!cleanNumber) {
        onChange?.("");
        return;
      }

      try {
        const dialCode = country.countryCallingCodes[0];
        const fullNumber = `${dialCode}${cleanNumber}`;

        if (PhoneHelpers.validateInternational(fullNumber, country.alpha2 as CountryCode)) {
          const formatted = PhoneHelpers.formatInternational(fullNumber, "E.164");
          onChange?.(formatted);
        } else {
          onChange?.(fullNumber);
        }
      } catch {
        const dialCode = country.countryCallingCodes[0];
        onChange?.(`${dialCode}${cleanNumber}`);
      }
    },
    [onChange],
  );

  const handlePhoneNumberChange = useCallback(
    (newPhoneNumber: string) => {
      setDisplayNumber(newPhoneNumber);

      const cleanNumber = newPhoneNumber.replace(/\D/g, "");
      setPhoneNumber(cleanNumber);

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        emitValue(cleanNumber, selectedCountry);
      }, 50);
    },
    [selectedCountry, emitValue],
  );

  const handleBlur = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = undefined;
    }

    if (phoneNumber) {
      emitValue(phoneNumber, selectedCountry);
    }
  }, [phoneNumber, selectedCountry, emitValue]);

  const handleCountrySelect = useCallback(
    (country: Country) => {
      setSelectedCountry(country);
      setOpen(false);

      if (phoneNumber) {
        emitValue(phoneNumber, country);
      }
    },
    [phoneNumber, emitValue],
  );

  return (
    <div ref={ref} className={cn("flex", error && "[&_button]:border-destructive [&_input]:border-destructive", className)}>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-auto gap-1 rounded-r-none border-r-0 px-2 font-normal hover:bg-background focus-visible:z-10 focus-visible:outline-[3px]"
            disabled={disabled}
            size="lg"
          >
            <CircleFlag countryCode={selectedCountry.alpha2.toLowerCase()} height={20} width={20} />
            <span className="text-muted-foreground text-sm">{selectedCountry.countryCallingCodes[0]}</span>
            <ChevronDownIcon
              size={14}
              className="shrink-0 text-muted-foreground/80"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <div className="self-stretch my-2 w-px bg-border" />
        <PopoverContent className="w-[280px] border-input p-0" align="start">
          <Command className="w-full max-h-[200px] sm:max-h-[270px]">
            <CommandList>
              <div className="sticky top-0 z-10 bg-popover">
                <CommandInput placeholder={resolvedSearchPlaceholder} />
              </div>
              <CommandEmpty>{resolvedNoResultsText}</CommandEmpty>
              <CommandGroup>
                {countryOptions.map((country) => (
                  <CommandItem
                    className="flex items-center w-full gap-2"
                    key={country.alpha3}
                    onSelect={() => handleCountrySelect(country)}
                  >
                    <div className="flex items-center flex-grow space-x-2 overflow-hidden">
                      <div className="inline-flex items-center justify-center w-5 h-5 shrink-0 overflow-hidden rounded-full">
                        <CircleFlag countryCode={country.alpha2.toLowerCase()} height={20} />
                      </div>
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                        {getLocalizedCountryName(country)}
                      </span>
                      <span className="text-xs text-muted-foreground">{country.countryCallingCodes[0]}</span>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4 shrink-0",
                        selectedCountry.alpha3 === country.alpha3 ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <InputMask
        className="rounded-l-none border-l-0"
        placeholder={maskPlaceholder}
        value={displayNumber}
        onChange={(_value: string | number, maskedValue: string) => handlePhoneNumberChange(maskedValue)}
        onBlur={handleBlur}
        disabled={disabled}
        mask={maskPattern}
      />
    </div>
  );
});

export const PhoneInput = PhoneInputComponent;
